import React, { useEffect, useState } from "react";
import config from "../../config";

// Model choices come from the admin-managed catalog (active or not — an
// admin agent may pin a model hidden from the user picker).
const MODELS_URL = config.api.baseUrl + config.api.endpoints.claudeAgent.adminModels;
const ALL_TOOLS = ["bash", "read", "write", "edit", "glob", "grep", "web_fetch", "web_search"];

const empty = {
  name: "", description: "", icon: "", color: "",
  model: "", system_prompt: "",
  tools_config: ALL_TOOLS.reduce((o, k) => ((o[k] = true), o), {}),
  mcp_servers: [],
  permission_policy: "always_allow",
  networking: { type: "unrestricted" },
  max_messages_per_user_per_day: null,
  max_tokens_per_user_per_day: null,
  max_concurrent_running_threads_per_user: 3,
};

export default function AgentForm({ value, onSave, onCancel }) {
  const [draft, setDraft] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [models, setModels] = useState([]);

  useEffect(() => {
    setDraft(value && value._id ? { ...empty, ...value } : { ...empty });
    setErr(null);
  }, [value]);

  useEffect(() => {
    fetch(MODELS_URL, { credentials: "include" })
      .then((r) => r.json())
      .then((j) => setModels(j.data || []))
      .catch(() => {});
  }, []);

  // New agent: default to the catalog's first model once it loads.
  useEffect(() => {
    if (!draft.model && models.length) setDraft((d) => (d.model ? d : { ...d, model: models[0].id }));
  }, [models, draft.model]);

  // Keep an edited agent's model selectable even if it has left the catalog.
  const modelOptions = draft.model && !models.some((m) => m.id === draft.model)
    ? [{ id: draft.model, label: draft.model }, ...models]
    : models;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const toggleTool = (k) =>
    setDraft((d) => ({ ...d, tools_config: { ...d.tools_config, [k]: !d.tools_config[k] } }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setSaving(true);
    try {
      // Agents no longer declare their own MCP servers — chats auto-bind the
      // servers the user has connected in their environment. Saving clears any
      // legacy declared servers.
      const payload = { ...draft, mcp_servers: [] };
      ["max_messages_per_user_per_day", "max_tokens_per_user_per_day", "max_concurrent_running_threads_per_user"]
        .forEach((k) => { payload[k] = payload[k] === "" || payload[k] === null ? null : Number(payload[k]); });
      await onSave(payload);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ca-modal-backdrop">
      <form className="ca-modal ca-form" onSubmit={submit}>
        <h3>{value && value._id ? "Edit Agent" : "New Agent"}</h3>
        {err && <div className="ca-admin-error">{err}</div>}
        <label>Name <input value={draft.name} onChange={(e) => set("name", e.target.value)} required /></label>
        <label>Description <input value={draft.description} onChange={(e) => set("description", e.target.value)} /></label>
        <div className="ca-form-row">
          <label>Icon (emoji) <input value={draft.icon || ""} onChange={(e) => set("icon", e.target.value)} maxLength={4} /></label>
          <label>Color <input type="color" value={draft.color || "#888888"} onChange={(e) => set("color", e.target.value)} /></label>
        </div>
        <label>Model
          <select value={draft.model} onChange={(e) => set("model", e.target.value)}>
            {modelOptions.map((m) => (
              <option key={m.id} value={m.id}>{m.label === m.id ? m.id : `${m.label} — ${m.id}`}</option>
            ))}
          </select>
        </label>
        <label>System prompt
          <textarea rows={6} value={draft.system_prompt} onChange={(e) => set("system_prompt", e.target.value)} />
        </label>
        <fieldset>
          <legend>Tools</legend>
          {ALL_TOOLS.map((t) => (
            <label key={t} className="ca-form-tool">
              <input type="checkbox" checked={!!draft.tools_config[t]} onChange={() => toggleTool(t)} /> {t}
            </label>
          ))}
        </fieldset>
        <fieldset>
          <legend>Networking</legend>
          <select value={draft.networking.type} onChange={(e) => set("networking", { type: e.target.value })}>
            <option value="unrestricted">unrestricted</option>
            <option value="restricted">restricted</option>
          </select>
        </fieldset>
        <fieldset>
          <legend>Caps</legend>
          <label>Messages/user/day <input type="number" min="0"
            value={draft.max_messages_per_user_per_day ?? ""}
            onChange={(e) => set("max_messages_per_user_per_day", e.target.value)} /></label>
          <label>Tokens/user/day <input type="number" min="0"
            value={draft.max_tokens_per_user_per_day ?? ""}
            onChange={(e) => set("max_tokens_per_user_per_day", e.target.value)} /></label>
          <label>Concurrent running threads/user <input type="number" min="1" max="20"
            value={draft.max_concurrent_running_threads_per_user ?? 3}
            onChange={(e) => set("max_concurrent_running_threads_per_user", e.target.value)} /></label>
        </fieldset>
        <fieldset>
          <legend>Permission policy</legend>
          <select value={draft.permission_policy} disabled>
            <option value="always_allow">always_allow (per-tool overrides coming soon)</option>
          </select>
        </fieldset>
        <div className="ca-form-actions">
          <button type="button" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
